import { motion, AnimatePresence } from 'framer-motion'
import { useState } from 'react'
import { universalThemes } from '@/data/frameworks'
import { cn } from '@/lib/utils'

export default function Step3() {
  const [activeTheme, setActiveTheme] = useState(universalThemes[0].id)
  const [expandedTradition, setExpandedTradition] = useState<string | null>(null)
  const [showDeepDive, setShowDeepDive] = useState(false)

  const selectedTheme = universalThemes.find(t => t.id === activeTheme)

  return (
    <div className="space-y-12">
      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-3xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 mb-6 shadow-divine">
          <span className="text-4xl">🔗</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-gold-gradient mb-6">
          Universal Themes of Wisdom
        </h1>
        <p className="text-lg text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
          Beneath the surface differences of world religions lie profound common truths.
          These universal themes unite humanity in our shared spiritual journey.
        </p>
      </motion.div>

      {/* Theme Selector */}
      <motion.div
        className="flex flex-wrap justify-center gap-4"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
        }}
      >
        {universalThemes.map((theme) => (
          <motion.button
            key={theme.id}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            whileHover={{ scale: 1.08, y: -2 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setActiveTheme(theme.id)
              setExpandedTradition(null)
              setShowDeepDive(false)
            }}
            className={cn(
              'px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2',
              activeTheme === theme.id
                ? 'bg-gold-500 text-white shadow-divine'
                : 'bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300 hover:bg-gold-200 dark:hover:bg-gold-800/40'
            )}
          >
            <span className="text-xl">{theme.icon}</span>
            <span>{theme.theme}</span>
          </motion.button>
        ))}
      </motion.div>

      {/* Selected Theme Details */}
      <AnimatePresence mode="wait">
        {selectedTheme && (
          <motion.div
            key={selectedTheme.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="sacred-card p-8 divine-glow"
          >
            <div className="text-center mb-8">
              <motion.span
                className="text-6xl mb-4 block"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ repeat: Infinity, duration: 2 }}
              >
                {selectedTheme.icon}
              </motion.span>
              <h2 className="font-display text-3xl font-bold text-gold-gradient mb-2">
                {selectedTheme.theme}
              </h2>
              <p className="text-lg text-gold-700/70 dark:text-gold-300/70 max-w-2xl mx-auto">
                {selectedTheme.description}
              </p>
            </div>

            {/* Deep Dive Toggle */}
            <motion.button
              onClick={() => setShowDeepDive(!showDeepDive)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full mb-6 px-4 py-2 text-sm font-medium rounded-lg bg-gold-50 dark:bg-gold-900/20 text-gold-600 dark:text-gold-400 border border-gold-200 dark:border-gold-800/30 transition-all duration-300"
            >
              {showDeepDive ? 'Hide Deep Analysis' : 'Deep Dive into This Theme'}
            </motion.button>

            <AnimatePresence>
              {showDeepDive && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden mb-6"
                >
                  <div className="p-4 rounded-lg bg-gold-50/50 dark:bg-gold-900/10 border border-gold-200/50 dark:border-gold-800/20">
                    <p className="text-sm text-gold-700/70 dark:text-gold-300/70 leading-relaxed">
                      The theme of <strong>{selectedTheme.theme}</strong> appears across virtually every major world tradition. While each tradition expresses this theme through its own theological vocabulary and cultural context, the underlying insight reveals a shared human concern that transcends geography, era, and language.
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Tradition teachings - Expandable */}
            <motion.div
              className="grid md:grid-cols-2 gap-6"
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
            >
              {selectedTheme.traditions.map((trad) => {
                const isExpanded = expandedTradition === trad.faith
                return (
                  <motion.div
                    key={trad.faith}
                    variants={{
                      hidden: { opacity: 0, y: 20 },
                      visible: { opacity: 1, y: 0 }
                    }}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setExpandedTradition(isExpanded ? null : trad.faith)}
                    className={cn(
                      'p-6 rounded-xl bg-gradient-to-br from-gold-50 to-white dark:from-gold-900/20 dark:to-divine-100 border border-gold-200 dark:border-gold-800/30 cursor-pointer transition-all duration-300',
                      isExpanded && 'ring-2 ring-gold-400 shadow-divine'
                    )}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-display font-semibold text-gold-800 dark:text-gold-200">
                        {trad.faith}
                      </h4>
                      <motion.span
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        className="text-gold-400 text-xs"
                      >
                        {'▼'}
                      </motion.span>
                    </div>
                    <blockquote className="font-serif text-lg italic text-gold-700 dark:text-gold-300 mb-3">
                      "{trad.teaching}"
                    </blockquote>
                    <cite className="text-sm text-gold-500 not-italic">
                      -- {trad.source}
                    </cite>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-4 pt-4 border-t border-gold-200 dark:border-gold-800/30">
                            <p className="text-sm text-gold-700/70 dark:text-gold-300/70">
                              This teaching from <strong>{trad.faith}</strong> shows how the theme of {selectedTheme.theme.toLowerCase()} is expressed uniquely within this tradition, drawing from its specific theological and cultural context.
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Visual Connection Diagram - Interactive */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="sacred-card p-8"
      >
        <h3 className="font-display text-2xl font-semibold text-center text-gold-800 dark:text-gold-200 mb-8">
          The Golden Threads
        </h3>

        <div className="relative">
          {/* Central unity symbol */}
          <div className="flex justify-center mb-8">
            <motion.div
              className="w-32 h-32 rounded-full bg-gradient-to-br from-gold-300 to-gold-600 flex items-center justify-center shadow-divine"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ repeat: Infinity, duration: 3 }}
            >
              <span className="text-5xl">✨</span>
            </motion.div>
          </div>

          {/* Connecting themes - Clickable */}
          <motion.div
            className="grid grid-cols-2 md:grid-cols-3 gap-8"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0 },
              visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
          >
            {universalThemes.map((theme) => (
              <motion.div
                key={theme.id}
                variants={{
                  hidden: { opacity: 0, scale: 0.8 },
                  visible: { opacity: 1, scale: 1 }
                }}
                whileHover={{ scale: 1.1, y: -4 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setActiveTheme(theme.id)
                  setExpandedTradition(null)
                  setShowDeepDive(false)
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }}
                className={cn(
                  'text-center cursor-pointer transition-all duration-300',
                  activeTheme === theme.id && 'transform scale-110'
                )}
              >
                <motion.div
                  className={cn(
                    'w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 text-3xl transition-all duration-300',
                    activeTheme === theme.id
                      ? 'bg-gold-400 dark:bg-gold-500 shadow-divine'
                      : 'bg-gold-100 dark:bg-gold-900/30'
                  )}
                  animate={activeTheme === theme.id ? { scale: [1, 1.1, 1] } : {}}
                  transition={{ repeat: activeTheme === theme.id ? Infinity : 0, duration: 1.5 }}
                >
                  {theme.icon}
                </motion.div>
                <h4 className="font-display font-medium text-gold-700 dark:text-gold-300 text-sm">
                  {theme.theme}
                </h4>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="mt-8 text-center">
          <p className="font-serif text-lg italic text-gold-700/70 dark:text-gold-300/70 max-w-2xl mx-auto">
            "Different paths, one destination. Many expressions, one truth.
            The divine speaks in countless languages, yet the message remains eternal."
          </p>
        </div>
      </motion.div>

      {/* Statistics - Animated */}
      <motion.div
        className="grid grid-cols-2 md:grid-cols-4 gap-6"
        initial="hidden"
        animate="visible"
        variants={{
          hidden: { opacity: 0 },
          visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
        }}
      >
        {[
          { number: '10,000+', label: 'Years of Wisdom' },
          { number: '5 Billion+', label: 'Followers Worldwide' },
          { number: '200+', label: 'Sacred Languages' },
          { number: '1', label: 'Universal Truth' }
        ].map((stat) => (
          <motion.div
            key={stat.label}
            variants={{
              hidden: { opacity: 0, y: 20 },
              visible: { opacity: 1, y: 0 }
            }}
            whileHover={{ scale: 1.05, y: -2 }}
            className="text-center p-6 sacred-card cursor-pointer transition-all duration-300"
          >
            <div className="font-display text-3xl font-bold text-gold-gradient mb-2">
              {stat.number}
            </div>
            <div className="text-sm text-gold-600/70 dark:text-gold-400/70">
              {stat.label}
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  )
}
